import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { getActivityId, getAuthToken, getBenchmarkId } from "../lib/authToken";
import { getApiUrls } from "../lib/apiUrl";
import { getCurrentPageLocation } from "../lib/eventer/assessmentEventLocation";
import { useAssessmentEventer } from "../lib/eventer/useAssessmentEventer";
import {
  fetchAssessmentContentRequest,
  initializeAssessmentTest,
  isAbortError,
  listAssessmentTestsRequest,
  saveSubmissionAnswerRequest,
  saveSubmissionAudioRequest,
  saveSubmissionProgressRequest,
  type AssessmentType,
  type FetchAssessmentContentParams,
  type TestInitializationState,
} from "../services/assessmentContentService";
import { useAssessmentSession } from "./assessmentUserContext";

type AssessmentContentContextValue = {
  listAssessmentTests: (params: {
    testType: AssessmentType;
    benchmark?: string;
    signal?: AbortSignal;
  }) => Promise<
    Array<{
      pk: string;
      sk: string;
      entity_type: string;
      grade: string;
      test_type: string;
      status?: string | null;
      benchmark?: string | null;
      updated_at?: string | null;
      question_count: number;
    }>
  >;
  initializedTests: Record<AssessmentType, TestInitializationState>;
  initializeTest: (
    testType: AssessmentType,
    location?: string,
  ) => Promise<void>;
  saveSubmissionAnswer: (params: {
    submissionId: string;
    questionId: string;
    answer: string | null;
    metadata?: object;
    location?: string;
  }) => Promise<void>;
  saveSubmissionAudio: (params: {
    submissionId: string;
    entityType: "oral_reading_audio" | "recap_audio";
    partOrder: number;
    questionId?: string;
    mimeType: string;
    blob: Blob;
    durationMs: number;
    location?: string;
  }) => Promise<void>;
  saveSubmissionProgress: (params: {
    submissionId: string;
    stepKey: string;
    location?: string;
  }) => Promise<void>;
  fetchAssessmentContent: <T = unknown>(
    params: FetchAssessmentContentParams,
  ) => Promise<T | null>;
};

type AssessmentContentProviderProps = {
  children: ReactNode;
};

const AssessmentContentContext = createContext<
  AssessmentContentContextValue | undefined
>(undefined);

const { assessmentsApiUrl: apiUrl } = getApiUrls();

function createInitialInitializedTests(): Record<
  AssessmentType,
  TestInitializationState
> {
  return {
    reading: {
      status: "idle",
      submissionId: null,
      testStatus: null,
      message: null,
      error: null,
    },
    listening: {
      status: "idle",
      submissionId: null,
      testStatus: null,
      message: null,
      error: null,
    },
  };
}

export function AssessmentContentProvider({
  children,
}: AssessmentContentProviderProps) {
  const { me } = useAssessmentSession();
  const trackEvent = useAssessmentEventer();
  const meId = me?.id ?? null;
  const meGrade = me?.grade ?? null;
  const meDistrictId = me?.district_id ?? null;
  const launchBenchmarkId = getBenchmarkId();
  const launchActivityId = getActivityId();
  const [initializedTests, setInitializedTests] = useState<
    Record<AssessmentType, TestInitializationState>
  >(createInitialInitializedTests);

  const trackContentEvent = useCallback(
    async (action: string, data?: Record<string, unknown>) => {
      const location =
        typeof data?.location === "string"
          ? data.location
          : getCurrentPageLocation();
      const eventData = { ...(data ?? {}) };
      delete eventData.location;

      await trackEvent({
        action,
        location,
        data: eventData,
      });
    },
    [trackEvent],
  );

  const initializeTest = useCallback(
    async (testType: AssessmentType, location?: string) => {
      setInitializedTests((current) => ({
        ...current,
        [testType]: {
          status: "loading",
          submissionId: null,
          testStatus: null,
          message: null,
          error: null,
        },
      }));

      try {
        const nextState = await initializeAssessmentTest({
          apiUrl,
          token: getAuthToken(),
          testType,
          me: {
            id: meId,
            grade: meGrade,
            district_id: meDistrictId,
            benchmark_id: launchBenchmarkId,
            activity_id: launchActivityId,
          },
        });

        setInitializedTests((current) => ({
          ...current,
          [testType]: nextState,
        }));
      } catch (error: unknown) {
        const nextState: TestInitializationState = {
          status: "error",
          submissionId: null,
          testStatus: null,
          message: null,
          error:
            error instanceof Error
              ? error.message
              : "Unable to initialize test.",
        };

        setInitializedTests((current) => ({
          ...current,
          [testType]: nextState,
        }));

        await trackContentEvent("assessment_initialize_failed", {
          testType,
          submissionId: nextState.submissionId,
          error: nextState.error ?? "Unknown error",
          location: location ?? `${testType}/welcome`,
        });
      }
    },
    [
      launchActivityId,
      launchBenchmarkId,
      meDistrictId,
      meGrade,
      meId,
      trackContentEvent,
    ],
  );

  const fetchAssessmentContent = useCallback(
    async <T = unknown,>({
      ...params
    }: FetchAssessmentContentParams): Promise<T | null> => {
      try {
        const result = await fetchAssessmentContentRequest<T>({
          apiUrl,
          token: getAuthToken(),
          ...params,
        });

        await trackContentEvent("assessment_content_loaded", {
          testType: params.testType,
          submissionId: params.submissionId,
          includePart: params.includePart ?? false,
          part: params.part ?? 1,
          isDemo: params.isDemo ?? false,
          location: params.location,
        });

        return result;
      } catch (error: unknown) {
        if (isAbortError(error)) {
          throw error;
        }

        await trackContentEvent("assessment_content_load_failed", {
          testType: params.testType,
          submissionId: params.submissionId,
          includePart: params.includePart ?? false,
          part: params.part ?? 1,
          isDemo: params.isDemo ?? false,
          location: params.location,
          error: error instanceof Error ? error.message : "Unknown error",
        });

        throw error;
      }
    },
    [trackContentEvent],
  );

  const listAssessmentTests = useCallback(
    async ({
      testType,
      benchmark,
      signal,
    }: {
      testType: AssessmentType;
      benchmark?: string;
      signal?: AbortSignal;
    }) =>
      listAssessmentTestsRequest({
        apiUrl,
        token: getAuthToken(),
        testType,
        benchmark,
        signal,
      }),
    [],
  );

  const saveSubmissionAnswer = useCallback(
    async ({
      submissionId,
      questionId,
      answer,
      metadata,
      location,
    }: {
      submissionId: string;
      questionId: string;
      answer: string | null;
      metadata?: object;
      location?: string;
    }) => {
      try {
        await saveSubmissionAnswerRequest({
          apiUrl,
          token: getAuthToken(),
          submissionId,
          questionId,
          answer,
          metadata,
        });

        await trackContentEvent("assessment_answer_saved", {
          submissionId,
          questionId,
          location,
        });
      } catch (error: unknown) {
        await trackContentEvent("assessment_answer_save_failed", {
          submissionId,
          questionId,
          location,
          error: error instanceof Error ? error.message : "Unknown error",
        });

        throw error;
      }
    },
    [trackContentEvent],
  );

  const saveSubmissionAudio = useCallback(
    async ({
      submissionId,
      entityType,
      partOrder,
      questionId,
      mimeType,
      blob,
      durationMs,
      location,
    }: {
      submissionId: string;
      entityType: "oral_reading_audio" | "recap_audio";
      partOrder: number;
      questionId?: string;
      mimeType: string;
      blob: Blob;
      durationMs: number;
      location?: string;
    }) => {
      try {
        await saveSubmissionAudioRequest({
          apiUrl,
          token: getAuthToken(),
          submissionId,
          entityType,
          partOrder,
          questionId,
          mimeType,
          blob,
          durationMs,
        });

        await trackContentEvent("assessment_audio_saved", {
          submissionId,
          entityType,
          partOrder,
          questionId,
          durationMs,
          location,
        });
      } catch (error: unknown) {
        await trackContentEvent("assessment_audio_save_failed", {
          submissionId,
          entityType,
          partOrder,
          questionId,
          durationMs,
          location,
          error: error instanceof Error ? error.message : "Unknown error",
        });

        throw error;
      }
    },
    [trackContentEvent],
  );

  const saveSubmissionProgress = useCallback(
    async ({
      submissionId,
      stepKey,
    }: {
      submissionId: string;
      stepKey: string;
    }) => {
      await saveSubmissionProgressRequest({
        apiUrl,
        token: getAuthToken(),
        submissionId,
        stepKey,
      });
    },
    [],
  );

  const value = useMemo(
    () => ({
      initializedTests,
      listAssessmentTests,
      initializeTest,
      saveSubmissionAnswer,
      saveSubmissionAudio,
      saveSubmissionProgress,
      fetchAssessmentContent,
    }),
    [
      initializedTests,
      listAssessmentTests,
      initializeTest,
      saveSubmissionAnswer,
      saveSubmissionAudio,
      saveSubmissionProgress,
      fetchAssessmentContent,
    ],
  );

  return (
    <AssessmentContentContext.Provider value={value}>
      {children}
    </AssessmentContentContext.Provider>
  );
}

export function useAssessmentContent(): AssessmentContentContextValue {
  const context = useContext(AssessmentContentContext);
  if (!context) {
    throw new Error(
      "useAssessmentContent must be used within AssessmentContentProvider.",
    );
  }
  return context;
}
